/**
 * Listens for font data sent from the SDK via postMessage and injects @font-face rules
 * using blob: URLs. This avoids CORS issues since blob URLs are same-origin to the iframe.
 *
 * Only activates when running inside an iframe (window.self !== window.top).
 * Deduplicates by family name so each font is only injected once.
 */

interface FontDataMessage {
    type: "caspeco-font-data";
    familyName: string;
    buffer: ArrayBuffer;
}

function listenForFontData() {
    // Only run inside an iframe
    if (window.self === window.top) return;

    const injectedFamilies = new Set<string>();

    function getOrCreateStyleElement(): HTMLStyleElement {
        const existing = document.getElementById("caspeco-font-faces") as HTMLStyleElement | null;
        if (existing) return existing;

        const style = document.createElement("style");
        style.id = "caspeco-font-faces";
        document.head.appendChild(style);
        return style;
    }

    window.addEventListener("message", (event: MessageEvent) => {
        const data = event.data as unknown as FontDataMessage;

        if (!event?.data || data.type !== "caspeco-font-data") return;

        const { familyName, buffer } = data;
        if (!familyName || !(buffer instanceof ArrayBuffer)) return;

        if (injectedFamilies.has(familyName)) return;
        injectedFamilies.add(familyName);

        const blob = new Blob([buffer], { type: "font/woff2" });
        const blobUrl = URL.createObjectURL(blob);

        const style = getOrCreateStyleElement();
        style.textContent += `@font-face { font-family: '${familyName}'; src: url('${blobUrl}'); font-display: swap; }\n`;
    });
}

export { listenForFontData };
